import { put, takeLatest } from "redux-saga/effects";

import {
  createDraftRequest,
  createDraftSuccess,
  createDraftFailure,

  fetchDraftsRequest,
  fetchDraftsSuccess,
  fetchDraftsFailure,

  updateDraftRequest,
  updateDraftSuccess,
  updateDraftFailure,

  deleteDraftRequest,
  deleteDraftSuccess,
  deleteDraftFailure,
} from "./draftsSlice";

import { fastApiURL } from "../../utils/config";
import { normalizeSolutionFormPayload } from "../../components/CreateSolutionField/solutionFormDefaults";
import TokenManager from "../../utils/tokenManager";

// =========================
// CREATE DRAFT
// =========================
function* createDraftSaga(action) {
  try {
    const token = TokenManager.getAccessToken();
    // Action payload now contains BOTH the form data and the projectId
    const { formData, projectId } = action.payload;

    const payload = normalizeSolutionFormPayload(formData);
    payload.project_id = projectId; // Inject project_id for the backend

    const response = yield fetch(`${fastApiURL}/api/drafts/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = yield response.json();
      throw new Error(errorData.detail ? JSON.stringify(errorData.detail) : "Failed to create draft.");
    }

    yield put(createDraftSuccess());
    
    // Refresh list, passing the projectId!
    yield put(fetchDraftsRequest(projectId)); 
  } catch (error) {
    console.error("Create Draft Error:", error);
    yield put(createDraftFailure(error.message));
  }
}

// =========================
// FETCH DRAFTS
// =========================
function* fetchDraftsSaga(action) {
  try {
    const token = TokenManager.getAccessToken();
    const projectId = action.payload; 

    if (!projectId) {
      throw new Error("projectId is required to fetch drafts.");
    }

    const response = yield fetch(
      `${fastApiURL}/api/drafts/?project_id=${projectId}&page=1&page_size=100`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = yield response.json();
      throw new Error(errorData.detail || "Failed to fetch drafts.");
    }

    const result = yield response.json();
    yield put(fetchDraftsSuccess(result.data));
  } catch (error) {
    console.error("Fetch Drafts Error:", error);
    yield put(fetchDraftsFailure(error.message));
  }
}

// =========================
// UPDATE DRAFT
// =========================
function* updateDraftSaga(action) {
  try {
    const token = TokenManager.getAccessToken();
    
    const { id, data, projectId } = action.payload; 

    const normalizedPayload = normalizeSolutionFormPayload(data);
    normalizedPayload.project_id = projectId; // Keep it attached

    const response = yield fetch(`${fastApiURL}/api/drafts/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(normalizedPayload),
    });

    if (!response.ok) {
      const errorData = yield response.json();
      const errorMessage = errorData.detail 
        ? (typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail))
        : "Failed to update.";
      throw new Error(errorMessage);
    }

    yield put(updateDraftSuccess());
    
    
    yield put(fetchDraftsRequest(projectId));
  } catch (error) {
    console.error("Update Draft Error:", error);
    yield put(updateDraftFailure(error.message));
  }
}
// =========================
// DELETE DRAFT
// =========================
function* deleteDraftSaga(action) {
  try {
    const token = TokenManager.getAccessToken();
    const { draftId, projectId } = action.payload;

    const response = yield fetch(`${fastApiURL}/api/drafts/${draftId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = yield response.json();
      throw new Error(errorData.detail || "Failed to delete draft.");
    }

    yield put(deleteDraftSuccess());
    
    yield put(fetchDraftsRequest(projectId));
  } catch (error) {
    console.error("Delete Draft Error:", error);
    yield put(deleteDraftFailure(error.message));
  }
}

export function* watchDraftsSaga() {
  yield takeLatest(
    createDraftRequest.type,
    createDraftSaga
  );

  yield takeLatest(
    fetchDraftsRequest.type,
    fetchDraftsSaga
  );

  yield takeLatest(
    updateDraftRequest.type,
    updateDraftSaga
  );

  yield takeLatest(
    deleteDraftRequest.type,
    deleteDraftSaga
  );
}